import './BundleList.sass';

import React, { FC, useMemo, memo } from 'react';
import { Block } from '$uikit';
import { useSelector } from 'react-redux';
import { bundleSelector } from '$store/bundles';
import { RootState } from '$store/rootReducer';
import { Currency, Slider } from '$shared/components';
import { IcPlusCircle28 } from '$assets';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { showModal } from '$navigation/helper';
import { statReachGoal } from '$utils';

const BundleListItemComponent: FC<{ id: number }> = ({ id }) => {
  const bundle = useSelector((root: RootState) => bundleSelector(root, id));

  const photo = useMemo(
    () =>
      [bundle.coverUrl, bundle.thumbnail]
        .filter(Boolean)
        .map((u) => `url(${u})`)
        .join(', '),
    [bundle],
  );

  const hasDiscount =
    bundle.priceWithoutDiscount && bundle.priceWithoutDiscount > bundle.price;

  return (
    <div
      className="BundleListItem"
      onClick={() => {
        showModal('bundle' + id);
        statReachGoal('bundles_bundle_list_item_click', { id });
      }}
    >
      <div className="BundleListItem__image" style={{ backgroundImage: photo }}>
        {hasDiscount ? (
          <div className="BundleListItem__prices">
            <div className="BundleListItem__price BundleListItem__price--old">
              <Currency amount={bundle.priceWithoutDiscount} currency={bundle.currency} />
            </div>
            <div className="BundleListItem__price BundleListItem__price--new">
              <Currency amount={bundle.price} currency={bundle.currency} />
            </div>
          </div>
        ) : (
          <div className="BundleListItem__price">
            <Currency amount={bundle.price} currency={bundle.currency} />
          </div>
        )}
      </div>
      <div className="BundleListItem__name">{bundle.name}</div>
    </div>
  );
};

export const BundleListItem = memo(BundleListItemComponent);

export const BundleList: FC<{ profileId: number; ids: number[] }> = ({
  profileId,
  ids,
}) => {
  const t = useTranslator();
  const currentUser = useCurrentUserInfo();
  const isMe = currentUser.id === profileId;

  return (
    <Block className="BundleList">
      <Slider headerText={t('bundles_list_title')}>
        {currentUser.verified === 2 && isMe && (
          <div
            className="BundleList__new"
            onClick={() => {
              statReachGoal('bundles_create_new');
              showModal('create_bundle');
            }}
          >
            <div className="BundleList__new__block">
              <IcPlusCircle28 />
            </div>
            <div className="BundleList__new__title">
              {t('bundles_list_create_button')}
            </div>
          </div>
        )}
        {[...ids].reverse().map((id) => (
          <BundleListItem key={id} id={id} />
        ))}
      </Slider>
    </Block>
  );
};
